/**
 * Preparation-request Notes API — append-only progress log for each prep sheet.
 *
 * The peer of fileNotesApi.js: it records human-written entries (Adviser / New
 * Business Assistant) and automatic system events, and is never edited or
 * deleted (FAIS audit-trail requirement). This is the notes surface §28.7 left
 * deferred; it carries the §31 acknowledgement model from the start.
 *
 * The only structural differences from file notes are the table
 * (prep_request_notes), the parent column (prep_request_id), and the role pair
 * that governs acknowledgement: {Adviser, New Business Assistant}.
 */

import supabase from './supabaseClient';

export const NOTE_BODY_MAX = 200;

/**
 * The two roles allowed on a preparation request (CLAUDE.md §24 / §31). A human
 * note is acknowledged by the OTHER of these two roles. As with file notes, the
 * routing is computed from the active UI role at write time (roles are never
 * stored in the DB), so it lives here rather than in a DB trigger.
 */
export const PREP_NOTE_ROLE_PAIR = ['adviser', 'new_business_assistant'];

/**
 * Given a note's author type and the role the author was acting as, decide
 * whether the note requires acknowledgement and, if so, which role must
 * acknowledge it. System notes never require acknowledgement, and a note whose
 * author role is not one of the prep's two roles requires none either
 * (defensive fallback — CLAUDE.md §31).
 */
export function computePrepNoteAck(authorType, authorRole) {
  if (authorType === 'system' || !PREP_NOTE_ROLE_PAIR.includes(authorRole)) {
    return { requiresAck: false, acknowledgerRole: null };
  }
  return {
    requiresAck: true,
    acknowledgerRole: PREP_NOTE_ROLE_PAIR.find((r) => r !== authorRole),
  };
}

function mapRowToNote(row) {
  return {
    id: row.id,
    prepRequestId: row.prep_request_id,
    authorType: row.author_type,
    authorId: row.author_id || null,
    authorDisplayName: row.author_display_name || '',
    body: row.body || '',
    createdAt: row.created_at,
    // Acknowledgement status (CLAUDE.md §31)
    requiresAck: row.requires_ack === true,
    acknowledgerRole: row.acknowledger_role || null,
    acknowledgedAt: row.acknowledged_at || null,
    acknowledgedBy: row.acknowledged_by || null,
  };
}

/**
 * Fetch all notes for a single prep request, oldest first (journal order).
 */
export async function getNotesForPrep(prepRequestId) {
  const { data, error } = await supabase
    .from('prep_request_notes')
    .select('*')
    .eq('prep_request_id', prepRequestId)
    .order('created_at', { ascending: true });

  if (error) throw new Error(`Failed to fetch prep notes: ${error.message}`);
  return (data || []).map(mapRowToNote);
}

/**
 * Fetch note counts for many prep requests in one query.
 *
 * Returns { counts, pendingByRole } — the prep-side peer of
 * fileNotesApi.getNoteCountsByFile: counts[prepId] is the total note count
 * (drives the "N notes" badge) and pendingByRole[prepId] maps each
 * acknowledger role to its count of unacknowledged notes. Card illumination
 * is OWNER-ONLY (CLAUDE.md §31): lit for a viewer ⇔
 * pendingByRole[prepId][activeRole] > 0.
 */
export async function getNoteCountsByPrep() {
  const { data, error } = await supabase
    .from('prep_request_notes')
    .select('prep_request_id, requires_ack, acknowledged_at, acknowledger_role');

  if (error) throw new Error(`Failed to fetch prep note counts: ${error.message}`);
  const counts = {};
  const pendingByRole = {};
  for (const row of data || []) {
    counts[row.prep_request_id] = (counts[row.prep_request_id] || 0) + 1;
    if (row.requires_ack === true && !row.acknowledged_at && row.acknowledger_role) {
      const byRole = pendingByRole[row.prep_request_id] || (pendingByRole[row.prep_request_id] = {});
      byRole[row.acknowledger_role] = (byRole[row.acknowledger_role] || 0) + 1;
    }
  }
  return { counts, pendingByRole };
}

/**
 * Fetch every prep note still awaiting acknowledgement BY the given role,
 * oldest first, with the parent prep sheet's client name embedded. Drives the
 * login catch-up gate (CLAUDE.md §31.9).
 */
export async function getPendingNotesForRole(role) {
  if (!role) return [];
  const { data, error } = await supabase
    .from('prep_request_notes')
    .select('*, preparation_requests(client_name, status)')
    .eq('requires_ack', true)
    .is('acknowledged_at', null)
    .eq('acknowledger_role', role)
    .order('created_at', { ascending: true });

  if (error) throw new Error(`Failed to fetch pending prep notes: ${error.message}`);
  return (data || []).map((row) => ({
    ...mapRowToNote(row),
    clientName: row.preparation_requests?.client_name || '',
    // Parent request's stored status — the login catch-up gate uses it to
    // leave notes on sealed Inbox requests out of the New Business
    // Assistant's list (Inbox draw gate, CLAUDE.md §40), exactly as the
    // file counterpart returns fileStatus for sealed files.
    prepStatus: row.preparation_requests?.status || null,
  }));
}

/**
 * Insert a new note. Body is trimmed and length-checked client-side (the DB
 * also enforces a 200-char ceiling — migration 29). Acknowledgement routing is
 * computed here from the author's active role (CLAUDE.md §31).
 */
export async function createNote({
  prepRequestId,
  authorType,
  authorId = null,
  authorDisplayName,
  body,
  authorRole = null,
}) {
  const trimmed = (body || '').trim();
  if (!trimmed) throw new Error('Note body is empty.');
  if (trimmed.length > NOTE_BODY_MAX) {
    throw new Error(`Note body exceeds ${NOTE_BODY_MAX} characters.`);
  }
  if (!authorType || (authorType !== 'user' && authorType !== 'system')) {
    throw new Error('authorType must be "user" or "system".');
  }
  if (!authorDisplayName) {
    throw new Error('authorDisplayName is required.');
  }

  const { requiresAck, acknowledgerRole } = computePrepNoteAck(authorType, authorRole);

  const { data, error } = await supabase
    .from('prep_request_notes')
    .insert({
      prep_request_id: prepRequestId,
      author_type: authorType,
      author_id: authorId,
      author_display_name: authorDisplayName,
      body: trimmed,
      requires_ack: requiresAck,
      acknowledger_role: acknowledgerRole,
    })
    .select()
    .single();

  if (error) throw new Error(`Failed to create prep note: ${error.message}`);
  return mapRowToNote(data);
}

/**
 * Convenience helper: insert a system-authored note.
 */
export async function createSystemNote(prepRequestId, body) {
  return createNote({
    prepRequestId,
    authorType: 'system',
    authorId: null,
    authorDisplayName: 'System',
    body,
  });
}

/**
 * Acknowledge a note. Routes through the acknowledge_prep_note() SECURITY
 * DEFINER RPC — the only write path for the acknowledgement columns, since
 * prep_request_notes has no UPDATE policy (append-only). Idempotent. Returns
 * the updated note (camelCase).
 */
export async function acknowledgeNote(noteId) {
  const { data, error } = await supabase.rpc('acknowledge_prep_note', { p_note_id: noteId });
  if (error) throw new Error(`Failed to acknowledge prep note: ${error.message}`);
  const row = Array.isArray(data) ? data[0] : data;
  return row ? mapRowToNote(row) : null;
}
